import type { RefObject, Dispatch, SetStateAction, ReactNode, ReactElement, ComponentProps, KeyboardEvent } from 'react'
import React, { Children, cloneElement, createElement, createRef, useEffect, useRef, useState, useContext } from 'react'
import { Tabs as ReachTabs, TabList, TabPanel, TabPanels } from '@reach/tabs'
import { css, cx } from '@linaria/core'
import { trackEvent } from '@fs/zion-analytics'
import { boolAttr, sanitizeProps } from '@fs/zion-frontend-friends'
import { useLocation } from '@fs/zion-router'
import Tab from './Tab'
import { Bleed } from '../bleed'
import { colors } from '../theme'
import themeSubsets from '../theme/themeSubsets'
import Elevation from '../elevation/Elevation'
import HorizontalScroller, { smartLeftScroller, smartRightScroller } from '../horizontal-scroller'
import { TabsListPortal } from './TabsPortalProvider'
import { sizeWidths } from '../layout-band/LayoutBand'
import { WhiteThemeContext } from '../theme/white-theme'

const tabListCss = css`
  color: ${colors.transparent.gray70};
  align-items: flex-end;
  display: flex;
  float: left;
  position: relative;
  transition: transform 0.2s ease-in-out;
  white-space: nowrap;

  &[centered] {
    margin: 0 auto;
  }

  &[fullwidth] {
    display: grid;
    grid-template-columns: repeat(var(--tab-count), minmax(max-content, 1fr));
    min-width: 100%;
  }
`

const tabListContainerCss = css`
  flex: 1 1;
  width: 0; /* Not sure why this works, but it allows the left and right sides to take priority and shrink the tab width. flex: 1 1 0 should work, but doesn't. */
  max-width: 100%;
`

const wrapperCss = css`
  & *[data-reach-tab-panel]:focus {
    outline: none;
  }
`

const tabsRow = css`
  display: flex;
`

function moveTabIntoView(
  tabRefs: RefObject<HTMLDivElement>[],
  currentIndex: number,
  parentEl: HTMLDivElement | null,
  setScroll: Dispatch<SetStateAction<number>>
): void {
  const currentTab = tabRefs[currentIndex].current
  const previousTab = tabRefs[currentIndex - 1]?.current
  const nextTab = tabRefs[currentIndex + 1]?.current
  const { x: itemNodeX } = currentTab?.getBoundingClientRect() || { x: 0 }
  const { x: parentX } = parentEl?.getBoundingClientRect() || { x: 0 }

  if (!currentTab || !parentEl) return

  const tabX = itemNodeX - parentX
  const currentScrollPos = parentEl.scrollLeft
  let newScrollPos = currentScrollPos
  // Going left
  if (tabX < 32 && previousTab) {
    newScrollPos = currentScrollPos - (previousTab.clientWidth / 2 - tabX) - 32
  } else if (tabX < 0) {
    newScrollPos = currentScrollPos + tabX
  } else if (tabX + currentTab.clientWidth > parentEl.clientWidth) {
    const nextTabWidth = nextTab?.clientWidth || 0
    const fullView = tabX + currentTab.clientWidth - parentEl.clientWidth
    newScrollPos = currentScrollPos + fullView + nextTabWidth / 2 + 32
  }
  if (currentScrollPos !== newScrollPos) {
    setScroll(newScrollPos)
  }
}

export interface TabsProps {
  /** Whether to spread tabs across the full width of the screen */
  fullWidth?: boolean

  /** Whether to center tabs on the tab bar */
  centered?: boolean

  /** The color of tabs when selected or hovered */
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple'

  /** Optional. Tracking name to log when a tab is clicked (overridden by individual tabs with a linkName prop) */
  linkName?: string

  /** The max width for the Tabs. Useful when "centered" with lots of Tab children */
  maxWidth?: 'nano' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

  /** The index of the currently selected tab */
  index?: number

  /** The index of the initially selected tab */
  defaultIndex?: number

  /** Function that runs when the selected tab changes */
  onChange?: (index: number) => void

  /** Whether the tabs are elevated */
  elevated?: boolean

  children: ReactNode
}

interface RenderTabsProps extends TabsProps {
  pathMap?: Record<string, number>
  location?: ReturnType<typeof useLocation>
}

function Tabs({ children, ...props }: TabsProps): JSX.Element {
  if (!children) children = [] // Default prop value doesn't fix null error
  const tabChildren = Children.toArray(children).filter(
    (child) => (child as ReactElement)?.type === Tab
  ) as ReactElement[]

  const pathMap: Record<string, number> = tabChildren.reduce((paths, tab, i) => {
    if (tab.props?.to) {
      paths[tab.props.to] = i
    }
    return paths
  }, {})

  return pathMap ? (
    <RouterTabs pathMap={pathMap} {...props}>
      {tabChildren}
    </RouterTabs>
  ) : (
    <RenderTabs {...props}>{tabChildren}</RenderTabs>
  )
}

function RenderTabs({
  fullWidth,
  centered,
  children,
  color = 'blue',
  linkName,
  maxWidth,
  index,
  onChange,
  defaultIndex = 0,
  elevated,
  location,
  pathMap,
  ...props
}: RenderTabsProps): JSX.Element {
  // only using defaultIndex if location is not set.
  let pathNumber = defaultIndex
  if (pathMap && location?.pathname && pathMap[location.pathname] >= 0) {
    pathNumber = pathMap[location.pathname]
  }

  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    []
  )

  const [selectedIndex, unsafeSetSelectedIndex] = useState(pathNumber)
  const setSelectedIndex: typeof unsafeSetSelectedIndex = (...args) =>
    mountedRef.current && unsafeSetSelectedIndex(...args)

  const [scrollPosition, setScrollPosition] = useState(0)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const isWhite = useContext(WhiteThemeContext)

  const tabRefs: RefObject<HTMLDivElement>[] = []

  // Add a ref to each Tab and keep an array of tabRefs - Used to scroll the selected item into view
  const tabs = Children.map(children, (tab: ReactElement, i) => {
    const tabRef = createRef<HTMLDivElement>()
    tabRefs.push(tabRef)
    const newProps = { ...tab.props, ref: tabRef }

    // use 'aria-current' attribute if selected tab is a link
    if (tab.props.to) {
      newProps['aria-selected'] = undefined
      if (i === selectedIndex) {
        newProps['aria-current'] = 'page'
      }
    }
    // If the tab doesn't have a linkName but tabs does, a link (with "to") will only track if we push the linkName onto props
    if (newProps.to && linkName && !newProps.linkName) {
      newProps.linkName = linkName
    }
    return cloneElement(tab, newProps)
  })

  // change roles from tablist/tabpanel to navigation/group when all tabs are links
  const allTabsAreLinks = tabs.length ? tabs.every((tab) => tab.props.to) : false

  // Create TabPanel elements for use with Reach-Tabs
  // Put the children of the selected tab into the panel - ignore all other children - probably for performance?
  const tabPanels = Children.map(children, (tab: ReactElement, i) =>
    i === selectedIndex
      ? createElement(
          TabPanel,
          {
            // eslint-disable-next-line react/no-array-index-key -- These tabs don't really change order, so keying by index is fine
            key: `tab_${i}`,
            role: allTabsAreLinks ? 'group' : 'tabpanel',
          },
          tab.props.children
        )
      : // eslint-disable-next-line react/no-array-index-key -- These tabs don't really change order, so keying by index is fine
        createElement(TabPanel, { key: `tab_${i}`, role: allTabsAreLinks ? 'group' : 'tabpanel' })
  )

  useEffect(() => {
    if (typeof index !== 'undefined') setSelectedIndex(index)
  }, [index])

  // select a tab when pressing enter/return key
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter') {
      ;(e.target as HTMLDivElement)?.click?.()
    }
  }

  return (
    <ReachTabs
      {...sanitizeProps(props)}
      className={wrapperCss}
      index={selectedIndex}
      defaultIndex={defaultIndex}
      onChange={(ind) => {
        setSelectedIndex(ind)
        moveTabIntoView(tabRefs, ind, scrollerRef.current, setScrollPosition)
        onChange && onChange(ind)

        // If linkName was supplied, and this isn't a link tab, track a click event on tab change
        // If the individual tab has a linkName prop, use that instead
        if (!tabs[ind]?.props?.to && (tabs[ind]?.props?.linkName || linkName)) {
          trackEvent({
            event_name: 'click_action',
            link_name: tabs[ind]?.props?.linkName || linkName,
          })
        }
      }}
    >
      <TabsListPortal>
        <Bleed sides>
          <Elevation
            className={cx(tabsRow, !isWhite && themeSubsets[color])}
            z={elevated ? 1 : 0}
            iswhite={boolAttr(isWhite)}
            style={{ maxWidth: maxWidth && sizeWidths[maxWidth] }}
          >
            <HorizontalScroller
              className={tabListContainerCss}
              setScrollPosition={setScrollPosition}
              scrollPosition={scrollPosition}
              scrollLeftFn={smartLeftScroller('[role="tab"], [role="link"]')}
              scrollRightFn={smartRightScroller('[role="tab"], [role="link"]')}
              ref={scrollerRef}
            >
              <TabList
                style={{ '--tab-count': tabs.length }}
                className={tabListCss}
                fullwidth={boolAttr(fullWidth)}
                centered={boolAttr(centered)}
                role={allTabsAreLinks ? 'navigation' : 'tablist'}
                onKeyDown={onKeyDown}
              >
                {tabs}
              </TabList>
            </HorizontalScroller>
          </Elevation>
        </Bleed>
      </TabsListPortal>
      {tabPanels && tabPanels.length > 0 && <TabPanels>{tabPanels}</TabPanels>}
    </ReachTabs>
  )
}

function RouterTabs(props: ComponentProps<typeof RenderTabs>): JSX.Element {
  return <RenderTabs {...props} location={useLocation()} />
}

export default Tabs
